import React, { useCallback } from 'react';

import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import { toDayJs } from '~/services/TimeService';

import { CatalogRoom, ConflictInfo, ConflictResolution, StopsellDateRange, StopsellRoom } from '~/types/stopsell';

import { pluralizeToString } from '~/utils/stringUtils';

import StopsellConflictBanner from './StopsellConflictBanner';
import StopsellResolvedBanner from './StopsellResolvedBanner';

export function formatDateRange(start: string, end: string): string {
  const s = toDayJs(start);
  const e = toDayJs(end);
  if (s.isSame(e, 'day')) {
    return s.format('DD MMM');
  }
  if (s.month() === e.month()) {
    return `${s.format('DD')}–${e.format('DD MMM')}`;
  }
  return `${s.format('DD MMM')} – ${e.format('DD MMM')}`;
}

function countNights(start: string, end: string): number {
  return toDayJs(end).diff(toDayJs(start), 'day');
}

interface Props {
  room: StopsellRoom;
  selected: Array<boolean>;
  catalogRooms: Array<CatalogRoom>;
  selectedRoomTypeId: string;
  blackoutDates: Array<StopsellDateRange>;
  conflicts: Array<Array<ConflictInfo>>;
  conflictResolutions: Array<Array<ConflictResolution | null>>;
  onChange: (dateIndex: number, value: boolean) => void;
  onToggleAll: (value: boolean) => void;
  onRoomTypeChange: (roomTypeId: string) => void;
  onResolve: (dateIndex: number, conflictIndex: number, status: ConflictResolution) => void;
  onUndo: (dateIndex: number, conflictIndex: number) => void;
}

export default function StopsellRoomGroup({
  room,
  selected,
  catalogRooms,
  selectedRoomTypeId,
  blackoutDates,
  conflicts,
  conflictResolutions,
  onChange,
  onToggleAll,
  onRoomTypeChange,
  onResolve,
  onUndo,
}: Props) {
  const nonConflictSelected = selected.filter((v, dateIndex) => {
    const dateConflicts = conflicts[dateIndex] ?? [];
    const dateResolutions = conflictResolutions[dateIndex] ?? [];
    const isDisabled = dateConflicts.some(
      (_, conflictIndex) => !dateResolutions[conflictIndex] || dateResolutions[conflictIndex] === 'ignored',
    );
    return !isDisabled && v;
  });
  const nonConflictTotal = selected.filter((_, dateIndex) => {
    const dateConflicts = conflicts[dateIndex] ?? [];
    const dateResolutions = conflictResolutions[dateIndex] ?? [];
    const isDisabled = dateConflicts.some(
      (_, conflictIndex) => !dateResolutions[conflictIndex] || dateResolutions[conflictIndex] === 'ignored',
    );
    return !isDisabled;
  });

  const allSelected = nonConflictTotal.length > 0 && nonConflictSelected.length === nonConflictTotal.length;
  const anySelected = nonConflictSelected.length > 0;
  const indeterminate = anySelected && !allSelected;

  const handleToggleAll = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onToggleAll(!allSelected);
    },
    [allSelected, onToggleAll],
  );

  const isMapped = !!selectedRoomTypeId;

  const today = toDayJs(new Date().toISOString().slice(0, 10));
  const futureBlackoutDates = blackoutDates.filter((d) => !toDayJs(d.end).isBefore(today, 'day'));
  const existingLabel =
    futureBlackoutDates.length > 0 ? futureBlackoutDates.map((d) => formatDateRange(d.start, d.end)).join(', ') : null;

  return (
    <Accordion defaultExpanded variant="outlined" disableGutters>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ bgcolor: 'grey.50' }}
        component="div"
        role="button"
        tabIndex={0}
      >
        <Stack direction="row" alignItems="center" gap={1} sx={{ flex: 1, mr: 1 }}>
          <Checkbox
            checked={allSelected}
            indeterminate={indeterminate}
            onChange={(e) => {
              e.stopPropagation();
              onToggleAll(e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
            size="small"
          />
          <Stack sx={{ flex: 1, minWidth: 0 }} gap={0.5}>
            <Typography fontWeight={500} variant="body2">
              {room.name}
            </Typography>
            {catalogRooms.length > 0 && (
              <Select
                size="small"
                displayEmpty
                value={selectedRoomTypeId}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => onRoomTypeChange(e.target.value)}
                error={!isMapped}
                sx={{ fontSize: 12, maxWidth: 320 }}
              >
                <MenuItem value="" disabled>
                  <Typography variant="body2" color="text.disabled">
                    Select room type…
                  </Typography>
                </MenuItem>
                {catalogRooms.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name}
                  </MenuItem>
                ))}
              </Select>
            )}
            {existingLabel && (
              <Stack direction="row" alignItems="center" gap={0.5}>
                <EventBusyOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  Existing: {existingLabel}
                </Typography>
              </Stack>
            )}
          </Stack>
          <Chip label={pluralizeToString('date range', room.dates.length)} size="small" sx={{ bgcolor: 'grey.200' }} />
          <Box>
            <Button size="small" onClick={handleToggleAll}>
              {allSelected ? 'Deselect all' : 'Select all dates'}
            </Button>
          </Box>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <Stack divider={<Divider />}>
          {room.dates.map((date, dateIndex) => {
            const dateConflicts = conflicts[dateIndex] ?? [];
            const dateResolutions = conflictResolutions[dateIndex] ?? [];
            const isDisabled = dateConflicts.some(
              (_, conflictIndex) => !dateResolutions[conflictIndex] || dateResolutions[conflictIndex] === 'ignored',
            );

            // Compute display date: expand for every conflict resolved as extending
            const rangesToMerge = [
              date,
              ...dateConflicts
                .filter((_, conflictIndex) => dateResolutions[conflictIndex] === 'extending')
                .map((c) => c.existingRange),
            ];
            const displayDate =
              rangesToMerge.length > 1
                ? {
                    start: rangesToMerge.reduce((min, r) => (r.start < min ? r.start : min), date.start),
                    end: rangesToMerge.reduce((max, r) => (r.end > max ? r.end : max), date.end),
                  }
                : date;

            const isIgnored = dateConflicts.length > 0 && dateResolutions.some((r) => r === 'ignored');

            return (
              <Stack key={dateIndex} sx={{ pl: 5, pr: 2, py: 0.75 }} gap={0.5}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Checkbox
                    checked={selected[dateIndex] ?? false}
                    onChange={(e) => onChange(dateIndex, e.target.checked)}
                    disabled={isDisabled}
                    size="small"
                  />
                  <Typography
                    variant="body2"
                    color={isIgnored ? 'text.disabled' : 'text.secondary'}
                    sx={isIgnored ? { textDecoration: 'line-through' } : undefined}
                  >
                    {formatDateRange(displayDate.start, displayDate.end)}
                  </Typography>
                  <Chip
                    label={pluralizeToString('night', countNights(displayDate.start, displayDate.end))}
                    size="small"
                    sx={{ bgcolor: 'warning.50', color: 'warning.dark', fontSize: 11 }}
                  />
                </Stack>

                {dateConflicts.map((conflict, conflictIndex) => {
                  const resolution = dateResolutions[conflictIndex] ?? null;
                  return resolution ? (
                    <StopsellResolvedBanner
                      key={conflictIndex}
                      conflict={conflict}
                      resolution={resolution}
                      onUndo={() => onUndo(dateIndex, conflictIndex)}
                    />
                  ) : (
                    <StopsellConflictBanner
                      key={conflictIndex}
                      conflict={conflict}
                      onResolve={(status) => onResolve(dateIndex, conflictIndex, status)}
                    />
                  );
                })}
              </Stack>
            );
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
